import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/cn';
import { loadTurnstile, type CaptchaChallenge } from '@/lib/turnstile';

export interface TurnstileWidgetProps {
  challenge: CaptchaChallenge;
  /** A solved token, or `null` once it expires or the check fails. */
  onToken: (token: string | null) => void;
  className?: string;
}

/**
 * Cloudflare's widget, rendered explicitly into a box of ours.
 *
 * A token is single use, so the form remounts this (a new `key`) after every
 * submit rather than calling `reset` on a widget it does not hold.
 */
export function TurnstileWidget({ challenge, onToken, className }: TurnstileWidgetProps) {
  const container = useRef<HTMLDivElement>(null);
  const callback = useRef(onToken);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    callback.current = onToken;
  }, [onToken]);

  useEffect(() => {
    let widgetId: string | null = null;
    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !container.current) return;
        widgetId = turnstile.render(container.current, {
          sitekey: challenge.siteKey,
          action: challenge.action,
          theme: 'auto',
          size: 'flexible',
          callback: (token) => callback.current(token),
          'expired-callback': () => callback.current(null),
          'error-callback': () => callback.current(null),
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (widgetId !== null) window.turnstile?.remove(widgetId);
    };
  }, [challenge.siteKey, challenge.action]);

  return (
    <div className={cn('min-h-[65px] w-full', className)}>
      <div ref={container} />
      {failed ? (
        <p role="alert" className="text-sm font-medium text-red-600">
          The security check could not load. Check your connection or disable any blocker, then
          reload the page.
        </p>
      ) : null}
    </div>
  );
}
